// ============================================================
// Story Detection — Task 1.3
// Segments the game into narrative phases based on eval
// trajectory and classifies the overall game arc.
// ============================================================

import type { MoveData, GamePhase, GameArc } from "@shared/types"

// --- Constants ---

/** Eval threshold (centipawns) for "significant" advantage */
const SIGNIFICANT_ADVANTAGE = 100

/** Eval threshold for "decisive" advantage */
const DECISIVE_ADVANTAGE = 300

/** Minimum eval swing (centipawns) for a "critical moment" */
const CRITICAL_SWING = 150

/** Number of moves typically considered "opening" */
const MIN_OPENING_MOVES = 6
const MAX_OPENING_MOVES = 15

/** Eval threshold for "equal" position */
const EQUAL_THRESHOLD = 50

// --- Helpers ---

function getAdvantage(
  evalScore: number
): "white" | "black" | "equal" {
  if (evalScore > EQUAL_THRESHOLD) return "white"
  if (evalScore < -EQUAL_THRESHOLD) return "black"
  return "equal"
}

function formatMoveRange(start: number, end: number): string {
  return `${start}-${end}`
}

function getEvalRange(moves: MoveData[]): [number, number] {
  if (moves.length === 0) return [0, 0]
  let min = Infinity
  let max = -Infinity
  for (const m of moves) {
    min = Math.min(min, m.eval)
    max = Math.max(max, m.eval)
  }
  return [min, max]
}

// --- Phase Detection ---

interface PhaseSegment {
  type: GamePhase["type"]
  startIndex: number
  endIndex: number
  moves: MoveData[]
}

/**
 * Detect where the opening ends.
 * Opening ends when:
 * 1. Eval deviates significantly from equal, OR
 * 2. We've passed MIN_OPENING_MOVES and see eval drift
 */
function detectOpeningEnd(moves: MoveData[]): number {
  if (moves.length <= MIN_OPENING_MOVES) return moves.length - 1

  for (let i = MIN_OPENING_MOVES; i < Math.min(moves.length, MAX_OPENING_MOVES); i++) {
    const evalAbs = Math.abs(moves[i].eval)
    // If eval has swung significantly, opening is over
    if (evalAbs > SIGNIFICANT_ADVANTAGE) return i - 1
    // If there's been a sharp change, opening is over
    if (Math.abs(moves[i].evalDelta) > CRITICAL_SWING) return i - 1
  }

  // Default: opening ends at MAX_OPENING_MOVES or total moves
  return Math.min(MAX_OPENING_MOVES, moves.length - 1)
}

/**
 * Check if the advantage flipped between two eval scores.
 */
function advantageFlipped(evalA: number, evalB: number): boolean {
  return (
    (evalA >= SIGNIFICANT_ADVANTAGE && evalB <= -SIGNIFICANT_ADVANTAGE) ||
    (evalA <= -SIGNIFICANT_ADVANTAGE && evalB >= SIGNIFICANT_ADVANTAGE)
  )
}

/**
 * Segment the game into narrative phases based on eval trajectory.
 */
export function detectPhases(moves: MoveData[]): GamePhase[] {
  if (moves.length === 0) return []

  const segments: PhaseSegment[] = []

  // Phase 1: Opening
  const openingEnd = detectOpeningEnd(moves)
  const openingMoves = moves.slice(0, openingEnd + 1)

  segments.push({
    type: "opening",
    startIndex: 0,
    endIndex: openingEnd,
    moves: openingMoves,
  })

  // Now analyze the rest of the game for middlegame, critical moments,
  // comebacks, and endgame

  let currentPhaseStart = openingEnd + 1
  let prevEval = moves[openingEnd]?.eval ?? 0
  let phaseMaxEval = prevEval
  let phaseMinEval = prevEval
  let currentAdvantage = getAdvantage(prevEval)
  let hasSeenAdvantageFlip = false

  // Track running phase type
  let currentPhaseType: GamePhase["type"] = "middlegame"

  for (let i = openingEnd + 1; i < moves.length; i++) {
    const move = moves[i]
    const evalNow = move.eval

    phaseMaxEval = Math.max(phaseMaxEval, evalNow)
    phaseMinEval = Math.min(phaseMinEval, evalNow)

    const newAdvantage = getAdvantage(evalNow)

    // Detect critical moment: large eval swing
    if (Math.abs(move.evalDelta) >= CRITICAL_SWING && i > currentPhaseStart) {
      // Close current phase
      const phaseSlice = moves.slice(currentPhaseStart, i)
      if (phaseSlice.length > 0) {
        segments.push({
          type: currentPhaseType,
          startIndex: currentPhaseStart,
          endIndex: i - 1,
          moves: phaseSlice,
        })
      }

      // Insert critical moment phase
      segments.push({
        type: "critical_moment",
        startIndex: i,
        endIndex: i,
        moves: [move],
      })

      // Check if advantage flipped
      if (advantageFlipped(prevEval, evalNow)) {
        hasSeenAdvantageFlip = true
        currentPhaseType = "comeback"
      } else if (Math.abs(evalNow) > DECISIVE_ADVANTAGE) {
        currentPhaseType = "middlegame_advantage"
      } else {
        currentPhaseType = "middlegame"
      }

      currentPhaseStart = i + 1
      phaseMaxEval = evalNow
      phaseMinEval = evalNow
      currentAdvantage = newAdvantage
    }
    // Detect when the advantage shifts (without a single critical swing)
    else if (newAdvantage !== currentAdvantage && newAdvantage !== "equal") {
      if (
        advantageFlipped(
          moves[currentPhaseStart]?.eval ?? 0,
          evalNow
        )
      ) {
        hasSeenAdvantageFlip = true
      }

      // If advantage shifted gradually, mark transition
      if (i - currentPhaseStart >= 3) {
        const phaseSlice = moves.slice(currentPhaseStart, i)
        if (phaseSlice.length > 0) {
          segments.push({
            type: currentPhaseType,
            startIndex: currentPhaseStart,
            endIndex: i - 1,
            moves: phaseSlice,
          })
        }

        if (hasSeenAdvantageFlip) {
          currentPhaseType = "comeback"
        } else if (Math.abs(evalNow) > DECISIVE_ADVANTAGE) {
          currentPhaseType = "middlegame_advantage"
        } else {
          currentPhaseType = "middlegame"
        }

        currentPhaseStart = i
        phaseMaxEval = evalNow
        phaseMinEval = evalNow
      }

      currentAdvantage = newAdvantage
    }

    prevEval = evalNow
  }

  // Close final phase
  if (currentPhaseStart < moves.length) {
    const remaining = moves.slice(currentPhaseStart)

    // Detect endgame: last portion of the game
    const isEndgame =
      moves.length - currentPhaseStart >= 4 &&
      currentPhaseStart > moves.length * 0.6

    segments.push({
      type: isEndgame ? "endgame" : currentPhaseType,
      startIndex: currentPhaseStart,
      endIndex: moves.length - 1,
      moves: remaining,
    })
  }

  // Add result phase
  const lastMove = moves[moves.length - 1]
  if (lastMove) {
    segments.push({
      type: "result",
      startIndex: moves.length - 1,
      endIndex: moves.length - 1,
      moves: [lastMove],
    })
  }

  // Convert segments to GamePhase objects
  return segments.map((seg) => {
    const firstMove = seg.moves[0]
    const lastSegMove = seg.moves[seg.moves.length - 1]
    const moveStart = firstMove?.moveNumber ?? 1
    const moveEnd = lastSegMove?.moveNumber ?? moveStart
    const evalRange = getEvalRange(seg.moves)

    // Determine phase advantage from average eval
    const avgEval =
      seg.moves.reduce((sum, m) => sum + m.eval, 0) / seg.moves.length

    return {
      type: seg.type,
      moves: formatMoveRange(moveStart, moveEnd),
      moveStart,
      moveEnd,
      summary: generatePhaseSummary(seg.type, seg.moves, evalRange),
      evalRange,
      advantage: getAdvantage(avgEval),
    }
  })
}

// --- Phase Summary Generation ---

function generatePhaseSummary(
  type: GamePhase["type"],
  moves: MoveData[],
  evalRange: [number, number]
): string {
  const first = moves[0]
  const last = moves[moves.length - 1]
  if (!first || !last) return ""

  switch (type) {
    case "opening": {
      const adv = getAdvantage(last.eval)
      const evalDesc =
        adv === "equal"
          ? "Position roughly equal"
          : `${adv === "white" ? "White" : "Black"} has a slight edge`
      return `Opening phase through move ${last.moveNumber}. ${evalDesc}.`
    }

    case "middlegame":
      return `Middlegame struggle. Eval ranges from ${formatEval(evalRange[0])} to ${formatEval(evalRange[1])}.`

    case "middlegame_advantage": {
      const side = evalRange[1] > 0 ? "White" : "Black"
      return `${side} builds a significant advantage. Eval reaches ${formatEval(Math.max(Math.abs(evalRange[0]), Math.abs(evalRange[1])))}.`
    }

    case "critical_moment": {
      const swing = last.evalDelta
      const side = last.color
      // Normalize to mover's perspective: evalDelta is from White's view
      const moverSwing = side === "white" ? swing : -swing
      const quality =
        moverSwing < -100
          ? `${side === "white" ? "White" : "Black"} makes a costly error`
          : `${side === "white" ? "White" : "Black"} finds a strong move`
      return `Critical moment on move ${last.moveNumber}. ${quality}.`
    }

    case "comeback": {
      const winner = last.eval > 0 ? "White" : "Black"
      return `${winner} mounts a comeback. The advantage flips dramatically.`
    }

    case "endgame": {
      const winner = last.eval > 0 ? "White" : "Black"
      const magnitude = Math.abs(last.eval)
      const desc =
        magnitude > DECISIVE_ADVANTAGE
          ? `${winner} is winning`
          : magnitude > SIGNIFICANT_ADVANTAGE
            ? `${winner} has the advantage`
            : "The position is roughly equal"
      return `Endgame phase. ${desc}.`
    }

    case "result":
      return "Game concludes."

    default:
      return ""
  }
}

function formatEval(cp: number): string {
  const pawns = (cp / 100).toFixed(1)
  return cp > 0 ? `+${pawns}` : pawns
}

// --- Arc Classification ---

/**
 * Classify the overall game arc based on eval trajectory.
 */
export function classifyGameArc(moves: MoveData[], result: string): GameArc {
  if (moves.length === 0) return "quiet_draw"

  const evals = moves.map((m) => m.eval)
  const swings = moves.map((m) => Math.abs(m.evalDelta))

  // Count significant swings
  const bigSwings = swings.filter((s) => s >= CRITICAL_SWING).length

  // Check for advantage flips
  let advantageFlips = 0
  for (let i = 1; i < evals.length; i++) {
    if (advantageFlipped(evals[i - 1], evals[i])) {
      advantageFlips++
    }
  }

  // Check if game was one-sided (eval mostly on one side)
  const maxEval = Math.max(...evals.map(Math.abs))

  // Draw check
  if (result === "1/2-1/2") {
    if (bigSwings <= 2 && maxEval < DECISIVE_ADVANTAGE) {
      return "quiet_draw"
    }
    // Even draws can be dramatic
    if (advantageFlips >= 2) return "tactical_battle"
  }

  // Blunder fest: many big swings
  if (bigSwings >= 6 && advantageFlips >= 3) {
    return "blunder_fest"
  }

  // Comeback: clear advantage flip late in the game
  if (advantageFlips >= 1) {
    // Find the latest flip
    let lastFlipIndex = 0
    for (let i = 1; i < evals.length; i++) {
      if (advantageFlipped(evals[i - 1], evals[i])) {
        lastFlipIndex = i
      }
    }
    // If the flip happened in the second half and the winner changed
    if (lastFlipIndex > moves.length * 0.3) {
      return "comeback"
    }
  }

  // Tactical battle: many significant moments but not pure blunders
  if (bigSwings >= 4 && advantageFlips <= 2) {
    return "tactical_battle"
  }

  // One-sided: eval consistently favors one side
  if (bigSwings <= 2 && maxEval > DECISIVE_ADVANTAGE) {
    return "one_sided"
  }

  // Dramatic finish: game was close until the end
  const lastFewMoves = moves.slice(-8)
  const lastFewSwings = lastFewMoves.filter(
    (m) => Math.abs(m.evalDelta) >= CRITICAL_SWING
  ).length
  if (lastFewSwings >= 2 && moves.length > 20) {
    return "dramatic_finish"
  }

  // Positional grind: slow, steady, not many fireworks
  if (bigSwings <= 3 && moves.length > 50) {
    return "positional_grind"
  }

  // Default: tactical battle (catch-all for complex games)
  return "tactical_battle"
}
